import { useCallback, useEffect, useRef } from "react";
import { useMutation } from "@tanstack/react-query";
import type { AppState } from "./types.ts";
import { hasInvalidDates } from "./format.ts";
import { saveState } from "./api.ts";

const SAVE_DEBOUNCE_MS = 400;

type Args = {
  companyId: string;
  state: AppState | null;
  dirty: boolean;
  onSavedVersion: (version: number, hadConcurrentMutation: boolean) => void;
  onConflict: () => void;
  onError: (msg: string) => void;
  onSavingChange: (saving: boolean) => void;
};

export function useAutosave({
  companyId,
  state,
  dirty,
  onSavedVersion,
  onConflict,
  onError,
  onSavingChange,
}: Args) {
  const stateRef = useRef(state);
  stateRef.current = state;
  const dirtyRef = useRef(dirty);
  dirtyRef.current = dirty;

  const saveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const inFlightRef = useRef<Promise<unknown> | null>(null);
  const savedVersionRef = useRef(0);
  const onErrorRef = useRef(onError);
  onErrorRef.current = onError;
  const mutationCounterRef = useRef(0);
  const counterAtSaveStartRef = useRef(0);
  const scheduleSaveRef = useRef<(delay?: number) => void>(() => {});

  const mutation = useMutation({
    mutationFn: async (snapshot: AppState) => {
      counterAtSaveStartRef.current = mutationCounterRef.current;
      const pending = saveState(companyId, snapshot);
      inFlightRef.current = pending;
      const result = await pending;
      if (!result.ok) throw result;
      savedVersionRef.current = result.version;
      return result.version;
    },
    onMutate: () => onSavingChange(true),
    onSettled: () => onSavingChange(false),
    onSuccess: (version) => {
      const concurrent =
        mutationCounterRef.current !== counterAtSaveStartRef.current;
      onSavedVersion(version, concurrent);
      if (dirtyRef.current && concurrent) scheduleSaveRef.current();
    },
    onError: (err) => {
      const status = (err as { status?: string })?.status;
      if (status === "conflict") {
        onError("Konflikt — laddar om");
        onConflict();
      } else {
        onError("Sparning misslyckades");
      }
    },
  });

  const isPendingRef = useRef(false);
  isPendingRef.current = mutation.isPending;

  const doSave = useCallback(() => {
    if (isPendingRef.current) return;
    if (!stateRef.current) return;
    if (!dirtyRef.current) return;
    if (hasInvalidDates(stateRef.current)) return;
    mutation.mutate(stateRef.current);
  }, [mutation]);

  const scheduleSave = useCallback(
    (delay = SAVE_DEBOUNCE_MS) => {
      if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
      saveTimerRef.current = setTimeout(() => {
        saveTimerRef.current = null;
        doSave();
      }, delay);
    },
    [doSave],
  );
  scheduleSaveRef.current = scheduleSave;

  const forceSave = useCallback(() => {
    if (saveTimerRef.current) {
      clearTimeout(saveTimerRef.current);
      saveTimerRef.current = null;
    }
    doSave();
  }, [doSave]);

  const noteMutation = useCallback(() => {
    mutationCounterRef.current++;
    scheduleSave();
  }, [scheduleSave]);

  useEffect(() => {
    if (!dirty) return;
    const handler = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = "";
    };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [dirty]);

  useEffect(() => {
    return () => {
      if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
      const snapshot = stateRef.current;
      if (!snapshot || !dirtyRef.current || hasInvalidDates(snapshot)) return;
      const inFlight = inFlightRef.current;
      void (async () => {
        await inFlight;
        const version = Math.max(savedVersionRef.current, snapshot.version);
        const result = await saveState(companyId, { ...snapshot, version });
        if (!result.ok) onErrorRef.current("Sparning misslyckades");
      })();
    };
  }, [companyId]);

  return { forceSave, noteMutation };
}
